import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import type { ForumLang } from "../constants/forumTranslations";

const LANG_STORAGE_KEY = "forumLang";
const BRAND_NAVY_LIGHT = "#2a2f55";

const PrivacyPolicy: React.FC = () => {
  const [lang, setLang] = useState<ForumLang>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === "es" || stored === "en") return stored;
    } catch {
      // ignore
    }
    return "es";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const isEs = lang === "es";

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col overflow-x-hidden">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-24 flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <Link to="/" className="flex items-center space-x-5">
              <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
                <img
                  src="/logo/logo.png"
                  alt="American School"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="border-l border-gray-100 pl-5 py-1">
                <h1
                  className="text-xl font-black tracking-tight uppercase leading-none"
                  style={{ color: "#161b3d" }}
                >
                  American School
                </h1>
                <p
                  className="text-xs font-bold mt-1 tracking-wider uppercase"
                  style={{ color: "#8b1e22" }}
                >
                  {isEs ? "Política de Privacidad" : "Privacy Policy"}
                </p>
              </div>
            </Link>
          </div>

          <div className="flex rounded border border-gray-200/80 bg-gray-50/60 p-px" role="group" aria-label={isEs ? "Idioma" : "Language"}>
            <button
              type="button"
              onClick={() => setLang("es")}
              className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                lang === "es"
                  ? "text-white"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
              }`}
              style={lang === "es" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                lang === "en"
                  ? "text-white"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
              }`}
              style={lang === "en" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      <motion.main
        className="flex-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-16">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-gray-900 text-center mb-3 uppercase">
            {isEs ? "Política de Privacidad" : "Privacy Policy"}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 text-center max-w-2xl mx-auto uppercase tracking-wide mb-10">
            {isEs
              ? "American School se compromete a proteger la privacidad y la información personal de los usuarios que utilizan esta plataforma."
              : "American School is committed to protecting the privacy and personal information of users of this platform."}
          </p>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 text-center">
                {isEs ? "Política de privacidad" : "Privacy Policy"}
              </h2>
            </div>

            <ol className="divide-y divide-gray-100">
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">1. {isEs ? "Información recopilada" : "Information Collected"}</p>
                <p className="mb-2">
                  {isEs
                    ? "Durante el proceso de inscripción y uso de la plataforma, podemos recopilar información personal como:"
                    : "During the enrollment process and use of the platform, we may collect personal information such as:"}
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-800">
                  <li>{isEs ? "Nombre del estudiante" : "Student's name"}</li>
                  <li>{isEs ? "Datos de contacto del responsable" : "Contact details of the responsible party"}</li>
                  <li>{isEs ? "Información necesaria para la facturación cuando se solicita comprobante fiscal" : "Information required for billing when a fiscal receipt is requested"}</li>
                </ul>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">2. {isEs ? "Uso de la información" : "Use of Information"}</p>
                <p className="mb-2">
                  {isEs
                    ? "La información recopilada se utiliza exclusivamente para:"
                    : "The information collected is used exclusively for:"}
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-800">
                  <li>{isEs ? "Gestionar inscripciones y registros de estudiantes" : "Managing enrollments and student records"}</li>
                  <li>{isEs ? "Procesar pagos correspondientes a las actividades ofrecidas" : "Processing payments for the activities offered"}</li>
                  <li>{isEs ? "Comunicarnos con los usuarios respecto a sus inscripciones o servicios relacionados" : "Communicating with users regarding their enrollments or related services"}</li>
                </ul>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">3. {isEs ? "Seguridad de los pagos" : "Payment Security"}</p>
                <p>
                  {isEs
                    ? "Los pagos realizados a través de esta plataforma son procesados mediante la pasarela de pago segura AZUL."
                    : "Payments made through this platform are processed via the secure AZUL payment gateway."}
                </p>
                <p className="mt-2">
                  {isEs
                    ? "American School no almacena ni tiene acceso a los datos completos de tarjetas de crédito o débito utilizados durante el proceso de pago."
                    : "American School does not store nor have access to the full credit or debit card data used during the payment process."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">4. {isEs ? "Protección de datos" : "Data Protection"}</p>
                <p>
                  {isEs
                    ? "American School adopta medidas razonables de seguridad para proteger la información personal contra accesos no autorizados, uso indebido o divulgación."
                    : "American School adopts reasonable security measures to protect personal information against unauthorized access, misuse or disclosure."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">5. {isEs ? "Contacto" : "Contact"}</p>
                <p>
                  {isEs
                    ? "Para cualquier consulta relacionada con esta política o con el manejo de datos personales, los usuarios pueden contactarnos a través del correo electrónico:"
                    : "For any inquiry related to this policy or the handling of personal data, users may contact us at the following email address:"}
                </p>
                <p className="mt-2">
                  <a href="mailto:info@americanschool.edu.do" className="text-[#5856d6] hover:text-[#3b3ab8] transition-colors">
                    info@americanschool.edu.do
                  </a>
                </p>
              </li>
            </ol>
          </div>
        </div>
      </motion.main>
    </div>
  );
};

export default PrivacyPolicy;
